import { useUserProfileContext } from "@context/user_profile";
import { formatDuration, parseFormat } from "@util/date";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@component/button";
import {
    interviewStatusAborted,
    interviewStatusComplete,
    interviewStatusInComplete,
    interviewStatusInterviewReady,
    interviewStatusNone,
    interviewStatusPartial,
    interviewStatusPendingInterview,
    interviewStatusPendingVerification,
} from "@constant/app";
import { Tooltip } from "@component/tooltip";
import {
    ArrowLeftIcon,
    CheckSquareIcon,
    ClockIcon,
    MessageSquareIcon,
    RefreshCcw,
    VideoIcon,
} from "lucide-react";
import { getStatusBadgeClass } from "@util/string";
import { useNavigate, useParams } from "react-router-dom";
import { InterviewInsight, InterviewVideo } from "@type/videoInterview";
import { getInterviewInsights, getInterviewStatus, getVideo } from "@service/interview";
import { InterviewPlayer } from "@component/interview-player";
import { CandidateInterviewStatus } from "@type/candidate";
import SubmittedRetakeDialog from "@component/submitted-retake-dialog";
import AuthLayout from "@container/layout/auth";
import CandidateAIInterviewInsights from "@component/candidate-interview-insights";
import { getAssessment, getUserInterviewSubmission } from "@service/assessment";
import { InterviewAssessment } from "@type/assessments";
import { catchErrorLog } from "@util/sentry";
import RetakeRequestDialogV2 from "@component/retake-request-v2-dialog";

export default function CandidateInterviewAssessmentPage() {
    const { interviewid } = useParams() || null;

    const navigate = useNavigate();
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const [isLoadingVideo, setLoadingVideo] = useState<boolean>(true);
    const [, setLoadingInterviewStatus] = useState<boolean>(true);
    const [videoInterview, setVideoInterview] = useState<InterviewVideo | undefined>(undefined);
    const [isRetakeDialogOpen, setIsRetakeDialogOpen] = useState<boolean>(false)
    const [isAlreadyRequestRetakeDialogOpen, setIsAlreadyRequestRetakeDialogOpen] = useState<boolean>(false)
    const [interviewStatus, setInterviewStatus] = useState<CandidateInterviewStatus | undefined>(undefined)
    const [, setLoadingInterviewInsight] = useState<boolean>(true);
    const [, setInterviewInsights] = useState<InterviewInsight | null>(null);
    const [videoDuration, setVideoDuration] = useState<number | null>(null);

    const [assessment, setAssessment] = useState<InterviewAssessment | null>(null);
    const [isLoadingInterviewDetails, setIsLoadingInterviewDetails] = useState(true);
    const [, setError] = useState<string | null>(null);

    const fetchAssessmentData = async () => {
        if (!interviewid) {
            setIsLoadingInterviewDetails(false);

            getInterviewStatus().then((resp) => {
                setInterviewStatus(resp.data);
                setLoadingInterviewStatus(false)
            }).catch((error) => {
                console.error('failed to get interview status', error)
                setLoadingInterviewStatus(false)
            })
            return;
        }

        setIsLoadingInterviewDetails(true);
        setError(null);

        try {
            const response = await getAssessment(interviewid as string);
            if (response) {
                const assessmentData = response;
                setAssessment(assessmentData as InterviewAssessment);
            } else {
                setError(response || "Failed to load your screening");
            }
        } catch (err) {
            catchErrorLog(`Error fetching assessment: ${err}`);
            setError("An unexpected error occurred");
        } finally {
            setIsLoadingInterviewDetails(false);
        }

        getInterviewStatus(interviewid as string).then((resp) => {
            setInterviewStatus(resp.data);
            setLoadingInterviewStatus(false)
        }).catch((error) => {
            catchErrorLog(`failed to get interview status: ${error}`);
            setLoadingInterviewStatus(false)
        })
    };

    // Fetch assessment data
    useEffect(() => {
        fetchAssessmentData();
    }, [interviewid]);

    useEffect(() => {
        if (!isLoadingUserProfile && assessment?.id && userInfo?.id) {
            getUserInterviewSubmission(assessment.id as string, userInfo?.id as string).then((resp) => {
                console.log("User Assessment Submission Response:", resp);
                return resp.conversation_id
            })
                .then((conversationId) => {
                    if (conversationId) {
                        // get insights
                        getInterviewInsights(conversationId).then((resp) => {
                            if (!resp || !resp.insights) {
                                setInterviewInsights(null);
                                setLoadingInterviewInsight(false);
                                return;
                            }
                            setInterviewInsights(JSON.parse(resp.insights));
                            setLoadingInterviewInsight(false);
                        }).catch((error) => {
                            catchErrorLog(`failed to get interview insights: ${error}`);
                            setLoadingInterviewInsight(false);
                        });

                        getVideo(conversationId).then((resp) => {
                            setVideoInterview(resp);
                            setLoadingVideo(false);
                        }).catch((error) => {
                            catchErrorLog(`failed to get video: ${error}`);
                            setLoadingVideo(false);
                        });
                    } else {
                        setLoadingVideo(false);
                    }
                })
                .catch((error) => {
                    catchErrorLog(`failed to get user assessment submission: ${error}`);
                });
        }
        else {
            if (userInfo && !interviewid) {
                console.log("Fetching video for first time interview with user ID:", userInfo.id);
                getVideo(undefined, "First Time Interview", userInfo.id).then((resp) => {
                    console.log("Conversation ID:", resp.conversation_id);
                    if (resp) {
                        setVideoInterview(resp);
                    }
                    setLoadingVideo(false)
                }).catch((error) => {
                    console.error('failed to get video', error)
                    setLoadingVideo(false);
                    setLoadingInterviewStatus(false);
                })
            }

        }
    }, [assessment, interviewid])

    const handleRetakeInterview = useCallback(() => {
        if (interviewStatus?.status === interviewStatusPendingVerification) {
            setIsAlreadyRequestRetakeDialogOpen(true);
        } else if (
            interviewStatus?.status === interviewStatusPendingInterview ||
            interviewStatus?.status === interviewStatusInterviewReady
        ) {
            if (!interviewid) {
                navigate("/ai-interview");
                return
            }
            navigate(`/ai-interview/${interviewid}`);
            return;
        } else {
            setIsRetakeDialogOpen(true);
        }
    }, [interviewStatus])

    if (isLoadingInterviewDetails) {
        return (
            <AuthLayout>
                <div className="flex items-center justify-center h-screen">
                    <div className="flex items-center space-x-2">
                        <svg
                            className="animate-spin h-5 w-5 text-gray-500 dark:text-gray-400"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            ></circle>
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        <div className="text-gray-600 dark:text-gray-400">Loading interview details...</div>
                    </div>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <div>
                <Button
                    variant="ghost"
                    size="sm"
                    className="mb-4 text-gray-600 hover:text-gray-900 hover:bg-gray-100 -ml-2"
                    onClick={() => navigate("/interview")}
                >
                    <ArrowLeftIcon className="h-4 w-4 mr-1" />
                    Back to Interviews
                </Button>
                <div className="flex flex-col sm:flex-row justify-between space-y-3 sm:space-y-0 mb-4">
                    <div className="flex flex-row items-center">
                        <MessageSquareIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">
                            {assessment ? assessment.content.interviewName : "AI Interview"}
                        </h3>
                    </div>
                    {(interviewStatus?.status !== interviewStatusComplete)
                        && (
                            <Button
                                variant="default"
                                onClick={handleRetakeInterview}
                            >
                                {(interviewStatus?.status === interviewStatusPendingInterview) ? (
                                    <>
                                        <VideoIcon />
                                        Take Interview
                                    </>
                                ) : (
                                    <>
                                        <RefreshCcw />
                                        Retake Interview
                                    </>
                                )}
                            </Button>
                        )}
                </div>
                <div className="space-y-6">
                    <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4">
                        <div className="flex justify-between items-center">
                            <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                                <CheckSquareIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                                Interview Status
                            </h4>
                            {interviewStatus?.status === interviewStatusComplete ? (
                                <span className={`px-2 py-1 ${getStatusBadgeClass(interviewStatus?.status || "")} rounded-full text-xs font-medium`}>
                                    Completed
                                </span>
                            ) : (
                                <Tooltip
                                    content={interviewStatus?.status === interviewStatusAborted ? "You completed less than 20% of the interview." : "You completed less than 50% of the interview."}
                                >
                                    <span className={`px-2 py-1 ${getStatusBadgeClass(interviewStatus?.status || "")} rounded-full text-xs font-medium`}>
                                        {interviewStatus?.status === interviewStatusInComplete
                                            ? "Incomplete"
                                            : (interviewStatus?.status === interviewStatusPendingInterview || interviewStatus?.status === interviewStatusPendingVerification)
                                                ? "Pending"
                                                : interviewStatus?.status}
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                        {interviewStatus?.status === interviewStatusComplete ? (
                            <>
                                <p className="text-sm mt-3 text-gray-600 dark:text-gray-400 mb-2">
                                    You have completed your AI Interview on{" "}
                                    {parseFormat(interviewStatus.updated_at || "", "h.mmA [on] D MMMM YYYY")}.
                                </p>
                                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                    <ClockIcon className="h-4 w-4 mr-1" />
                                    Duration: {videoDuration ? formatDuration(videoDuration) : (
                                        <div className="flex items-center">
                                            <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-blue-600 ml-1" />
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (interviewStatus?.status === interviewStatusNone || interviewStatus?.status === interviewStatusPendingVerification) ? (
                            <p className="text-sm mt-3 text-gray-600">
                                You are in the process of retaking your AI Interview.
                            </p>
                        ) : (interviewStatus?.status === interviewStatusPendingInterview && !interviewStatus?.reason) ? (
                            <p className="text-sm mt-3 text-gray-600">
                                You have not started your AI Interview yet.
                            </p>
                        ) : (interviewStatus?.status === interviewStatusInComplete) ? (
                            <p className="text-sm mt-3 text-gray-600">
                                You attempted the AI Interview on {parseFormat(interviewStatus?.updated_at || "", "D MMMM YYYY")} but did not complete.
                            </p>
                        ) : (interviewStatus?.status === interviewStatusAborted) ? (
                            <p className="text-sm mt-3 text-gray-600">
                                You attempted the AI interview on {parseFormat(interviewStatus?.updated_at || "", "D MMMM YYYY")} but aborted.
                            </p>
                        ) : (interviewStatus?.status === interviewStatusPartial) && (
                            <p className="text-sm mt-3 text-gray-600">
                                You attempted the AI interview on {parseFormat(interviewStatus?.updated_at || "", "D MMMM YYYY")} but partial.
                            </p>
                        )}
                    </div>
                    <CandidateAIInterviewInsights
                        tips={[
                            "Test your equipment 15 minutes before your next interview to ensure everything works properly.",
                            "Consider using headphones to improve audio quality and reduce echo.",
                            "Practice answering common interview questions to build confidence and improve your responses.",
                            "Practice speaking at a slightly slower pace to improve clarity, especially when discussing technical topics.",
                            "Ensure you are in a well-lit area to enhance video quality and visibility.",
                        ]}
                        height="600px"
                        conversation_id={videoInterview?.conversation_id || ""}
                        interviewStatus={interviewStatus?.status || ""}
                    ></CandidateAIInterviewInsights>
                    <InterviewPlayer
                        isLoadingVideo={isLoadingVideo}
                        interviewStatus={interviewStatus}
                        s3VideoLink={videoInterview?.s3_link || ""}
                        videoInterview={videoInterview as InterviewVideo}
                        setVideoDuration={(newDuration: number) => setVideoDuration(newDuration)}
                    />
                </div>
            </div>

            <RetakeRequestDialogV2
                isOpen={isRetakeDialogOpen}
                onClose={() => setIsRetakeDialogOpen(false)}
                onSubmit={() => {
                    fetchAssessmentData();

                    if (!interviewid) {
                        navigate("/ai-interview");
                        return
                    }
                    navigate(`/ai-interview/${interviewid}`);
                }}
            />

            <SubmittedRetakeDialog
                isOpen={isAlreadyRequestRetakeDialogOpen}
                onClose={() => { setIsAlreadyRequestRetakeDialogOpen(false) }}
            />
        </AuthLayout>
    )
}